import React, { useCallback, useEffect, useState } from 'react';
import { IoTimerOutline } from 'react-icons/io5';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Text } from 'rizzui';
import WidgetCard from '../../../components/card/WidgetCard';
import { CustomBadge } from '../../../components/forms/CustomBadge';
import Icon from '../../../components/icon/Icon';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { useDebounce } from '../../../hooks/useDebounce';
import { FilterOptions } from '../../../hooks/useFilters';
import Page from '../../../layout/Page/Page';
import { RootState } from '../../../redux/store';
import { menuRoutes } from '../../../router/menu';
import { useRankingsService } from '../../../services/rankings/useRankingsService';
import RankingClassificationModal from '../../users/profile/views/rankings/RankingClassificationModal';

interface Ranking {
    id: string;
    name: string;
    description?: string;
    userCount: number;
    active: boolean;
    resetInterval: string;
}

interface RankingsListWrapperProps {
    filters: FilterOptions;
    resetFilters: (limit?: number, hardReset?: boolean) => void;
}

const RankingsListWrapper: React.FC<RankingsListWrapperProps> = ({ filters, resetFilters }) => {

    const { getAllRankings } = useRankingsService();
    const { user } = useSelector((state: RootState) => state.auth);

    const [rankings, setRankings] = useState<Ranking[]>([]);
    const [filteredRankings, setFilteredRankings] = useState<Ranking[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRankingId, setSelectedRankingId] = useState<string | null>(null);
    const [selectedRankingTitle, setSelectedRankingTitle] = useState<string>('');

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES Función para obtener los rankings desde el servicio, aplicando los filtros seleccionados.
     * @EN Function to fetch rankings from the service, applying the selected filters.
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const fetchRankings = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Prepare backend filters based on the current filter options
            const backendFilters: any = {};

            if (filters.filter_filters?.search_text) {
                backendFilters.search_text = filters.filter_filters.search_text;
            }
            if (filters.filter_filters?.reset_interval) {
                backendFilters.reset_interval = filters.filter_filters.reset_interval;
            }
            if (filters.filter_filters?.active !== undefined && filters.filter_filters?.active !== '') {
                backendFilters.active = filters.filter_filters.active === 'true' || filters.filter_filters.active === true;
            }

            const response = await getAllRankings({ ...filters, filter_filters: backendFilters });
            let rankingsData = response.data || [];

            // Apply sorting by name (case insensitive, locale-aware)
            rankingsData.sort((a: Ranking, b: Ranking) => {
                const nameA = a.name?.toLowerCase() || '';
                const nameB = b.name?.toLowerCase() || '';
                return nameA.localeCompare(nameB, 'es', {
                    sensitivity: 'base',
                    numeric: true
                });
            });

            setRankings(rankingsData);
            setFilteredRankings(rankingsData);
        } catch (err: any) {
            setError(err.message || 'Error al cargar los rankings');
        } finally {
            setLoading(false);
        }
    }, [getAllRankings, filters.filter_filters?.search_text, filters.filter_filters?.reset_interval, filters.filter_filters?.active]);
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES Función para manejar el clic en un ranking y abrir el modal de clasificación.
     * @EN Function to handle ranking click and open the classification modal.
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleRankingClick = (rankingId: string, rankingTitle: string) => {
        setSelectedRankingId(rankingId);
        setSelectedRankingTitle(rankingTitle);
        setIsModalOpen(true);
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES Función para cerrar el modal de clasificación.
     * @EN Function to close the classification modal.
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRankingId(null);
        setSelectedRankingTitle('');
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES Función para obtener la etiqueta legible del intervalo de reinicio.
     * @EN Function to get the readable label for the reset interval.
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const getResetIntervalLabel = (interval: string) => {
        const labels: { [key: string]: string } = {
            'daily': 'Diario',
            'weekly': 'Semanal',
            'monthly': 'Mensual',
            'custom': 'Personalizado',
        };
        return labels[interval] || interval;
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    // Debounced fetch for search text
    const debouncedFetchRankings = useDebounce(fetchRankings, 500);

    // Unified effect to handle all filter changes
    useEffect(() => {
        // If there is search text, use debounce
        if (filters.filter_filters?.search_text !== undefined) {
            debouncedFetchRankings();
        } else {
            // For other filters, immediate fetch
            fetchRankings();
        }
    }, [filters.filter_filters?.search_text, filters.filter_filters?.reset_interval, filters.filter_filters?.active, fetchRankings, debouncedFetchRankings]);

    if (loading) {
        return (
            <Page container='fluid' className='space-y-6'>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center space-y-4">
                        <Loader />
                        <div className="space-y-2">
                            <h3 className="text-lg font-medium text-gray-700">
                                Cargando Rankings
                            </h3>
                            <p className="text-sm text-gray-500">
                                Por favor espera mientras obtenemos los datos...
                            </p>
                        </div>
                    </div>
                </div>
            </Page>
        );
    }

    if (error) {
        return (
            <Page container='fluid' className='space-y-6'>
                <div className="flex items-center justify-center min-h-[400px]">
                    <WidgetCard title="Error" className="text-center max-w-md">
                        <div className="space-y-4">
                            <p className="text-red-600 font-medium">{error}</p>
                            <Button
                                onClick={() => window.location.reload()}
                                className="mt-4"
                                variant="solid"
                            >
                                Reintentar
                            </Button>
                        </div>
                    </WidgetCard>
                </div>
            </Page>
        );
    }

    if (rankings.length === 0) {
        return (
            <Page container='fluid' className='space-y-6'>
                <div className="flex items-center justify-center min-h-[400px]">
                    <WidgetCard title="Sin Rankings" className="text-center max-w-md">
                        <div className="space-y-4">
                            <p className="text-gray-500">No hay rankings disponibles en este momento.</p>
                            <Button
                                onClick={() => resetFilters()}
                                variant="outline"
                                className="mt-4"
                            >
                                Limpiar Filtros
                            </Button>
                        </div>
                    </WidgetCard>
                </div>
            </Page>
        );
    }

    return (
        <Page container='fluid' className='space-y-6'>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredRankings.map((ranking) => (
                    <div key={ranking.id}>
                        <WidgetCard
                            title=""
                            headerClassName="hidden"
                            className="hover:shadow-lg transition-all duration-300 cursor-pointer"
                            onClick={() => handleRankingClick(ranking.id, ranking.name)}
                        >
                            <div className="space-y-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg font-bold text-gray-900 truncate mb-1">
                                            <Link to={`${menuRoutes.rankings.path}/${ranking.id}/info`} className='hover:text-primary'>
                                                {ranking.name}
                                            </Link>
                                        </h3>
                                        {ranking.description && (
                                            <Text className="text-sm text-gray-600 line-clamp-2">
                                                {ranking.description}
                                            </Text>
                                        )}
                                    </div>
                                    <CustomBadge
                                        color={ranking.active ? 'primary' : 'warning'}
                                        variant="solid"
                                        size="sm"
                                    >
                                        {ranking.active ? 'Activo' : 'Inactivo'}
                                    </CustomBadge>
                                </div>

                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                    <div className="flex items-center gap-1">
                                        <Icon icon="Group" className="w-4 h-4 text-gray-500" />
                                        <span>{ranking.userCount} {ranking.userCount === 1 ? 'usuario' : 'usuarios'}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <IoTimerOutline className="w-4 h-4" />
                                        <span>{getResetIntervalLabel(ranking.resetInterval)}</span>
                                    </div>
                                </div>
                            </div>
                        </WidgetCard>
                    </div>
                ))}
            </div>

            <RankingClassificationModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                rankingId={selectedRankingId || ''}
                rankingTitle={selectedRankingTitle}
                currentUserId={user?.id || ''}
            />
        </Page>
    );
};

export default RankingsListWrapper;